import { NavLink, useLocation } from "@remix-run/react";
import { MdTravelExplore } from "react-icons/md";
import { PiChatTeardropTextDuotone } from "react-icons/pi";
import { GearSix, User } from "phosphor-react";
import { FBProfile } from "~/.server/api";
import { cn } from "~/lib/utils";
import { Modal, useModal } from "~/components";
import GetCreditsModal from "~/components/modals/get-credits";
import MobileVerifyEmailBanner from "./mobile-verify-email-banner";

interface MobileFooterProps {
  messageCount: number;
  user?: FBProfile; // Made optional
}

export default function MobileFooter({
  messageCount,
  user, // Now optional
}: MobileFooterProps) {
  const creditsModal = useModal();
  const location = useLocation();

  const iconSize = 30;

  // Mirror the desktop avatar flame: while the unlimited window is active,
  // overlay a pulsing 🔥 on the My Profile tab so the boost cue is visible
  // from any page on mobile (no avatar lives in the header).
  const isUnlimitedActive = Boolean(
    user?.unlimited_messages_until &&
      new Date(user.unlimited_messages_until).getTime() > Date.now(),
  );

  // Determine if we should show the verify email banner
  // Hide on chat routes to prevent overlap with input bar
  const isChatRoute = location.pathname.includes("/messages/");
  const showVerifyEmailBanner = user && !user.metadata?.emailVerified && !isChatRoute;

  return (
    <>
      <div
        className={cn(
          "fixed bottom-0 left-0 right-0 z-50 flex bg-background border-t border-white/5",
          "min-h-16 md:min-h-full", // Adjust height for md screens
          "justify-around items-center", // Default layout for non-md screens
          "md:flex-col md:justify-between md:relative md:border-none md:bg-background md:backdrop-filter-none" // Reset for desktop (sidebar)
        )}
      >
        {/* Icons Section */}
        <div
          className={cn(
            "flex w-full md:flex-col md:items-center md:gap-16 md:pt-6",
            "md:mt-4 md:mb-auto justify-evenly" // Evenly space items horizontally
          )}
        >
          {/* Discover */}
          <NavLink
            to="/landing-pub"
            className={({ isActive }) =>
              cn(
                // Each nav item is flex-1 so it occupies equal space in the row
                "flex-1 flex flex-col items-center cursor-pointer text-xs md:text-base space-y-1 hover:scale-105 transition-transform transform duration-300",
                isActive
                  ? isUnlimitedActive
                    ? "text-emerald-400"
                    : "text-primary"
                  : "text-text1"
              )
            }
            viewTransition
          >
            {({ isActive }) => (
              <>
                <MdTravelExplore size={iconSize} />
                <span
                  className={cn(
                    isActive
                      ? isUnlimitedActive
                        ? "text-emerald-300"
                        : "text-text"
                      : "text-text1"
                  )}
                >
                  Discover
                </span>
              </>
            )}
          </NavLink>

          {/* Messages — when on this route during an active unlimited
              window, the highlight color flips from primary to emerald so
              the "message freely" affordance reads as one with the boost
              theme (send button, timer pill, BOOST ACTIVE badge). */}
          <NavLink
            to="/messages"
            className={({ isActive }) =>
              cn(
                "flex-1 flex flex-col items-center cursor-pointer text-xs md:text-base space-y-1 hover:scale-105 transition-transform transform duration-300",
                isActive
                  ? isUnlimitedActive
                    ? "text-emerald-400"
                    : "text-primary"
                  : "text-text1"
              )
            }
            viewTransition
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <PiChatTeardropTextDuotone size={iconSize} />
                  {messageCount > 0 && (
                    <span
                      className={cn(
                        "absolute md:-top-1 -top-0 left-5 transform",
                        // Make the badge a small circle with minimal padding
                        "flex items-center justify-center h-4 min-w-[1.5rem]",
                        "text-[0.6rem] font-bold border border-text1 rounded-full",
                        "bg-gradient-to-t from-red-600 to-rose-700 text-white leading-none",
                        "animate-pulsesubtle"
                      )}
                    >
                      {messageCount}
                    </span>
                  )}
                </div>
                <span
                  className={cn(
                    isActive
                      ? isUnlimitedActive
                        ? "text-emerald-300"
                        : "text-text"
                      : "text-text1"
                  )}
                >
                  Messages
                </span>
              </>
            )}
          </NavLink>

          {/* My Profile */}
          <NavLink
            to="/my-profile"
            className={({ isActive }) =>
              cn(
                "flex-1 flex flex-col items-center cursor-pointer text-xs md:text-base space-y-1 hover:scale-105 transition-transform transform duration-300",
                isActive
                  ? isUnlimitedActive
                    ? "text-emerald-400"
                    : "text-primary"
                  : "text-text1"
              )
            }
            viewTransition
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <User size={iconSize} weight="duotone" />
                  {isUnlimitedActive && (
                    <span
                      className="absolute -top-1 -right-1 text-[12px] leading-none drop-shadow-[0_0_6px_rgba(249,115,22,0.9)] animate-pulse pointer-events-none"
                      style={{ animationDuration: "1.5s" }}
                      aria-hidden
                    >
                      🔥
                    </span>
                  )}
                </div>
                {/* Badge priority: FREE BOOST during an active window
                    overrides the FREE CREDITS prompt, since the user's
                    profile is already being boosted. */}
                {isUnlimitedActive ? (
                  <>
                    <span
                      className={`hidden md:block ${isActive ? "text-text" : "text-text1"
                        }`}
                    >
                      My Profile
                    </span>
                    <span className="text-white text-[0.65rem] font-bold bg-gradient-to-t from-teal-600 to-emerald-500 rounded-xl px-1 shadow-md">
                      BOOST ACTIVE
                    </span>
                  </>
                ) : user && !user.metadata?.profileCompleted ? ( // Added null-check for user and metadata
                  // Show "FREE CREDITS" text if not completed
                  <>
                    <span
                      className={`hidden md:block ${isActive ? "text-text" : "text-text1"
                        }`}
                    >
                      My Profile
                    </span>
                    <span className="text-white text-[0.65rem] font-bold bg-gradient-to-t from-rose-700 to-red-600 rounded-xl px-1 shadow-md">
                      FREE CREDITS
                    </span>
                  </>
                ) : (
                  <span
                    className={cn(
                      isActive
                        ? isUnlimitedActive
                          ? "text-emerald-300"
                          : "text-primary"
                        : "text-text1"
                    )}
                  >
                    My Profile
                  </span>
                )}
              </>
            )}
          </NavLink>
        </div>

        {/* Settings - Desktop only */}
        <div className="hidden md:block md:mb-10">
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center cursor-pointer text-xs md:text-base space-y-1 hover:scale-105 transition-transform transform duration-300",
                isActive
                  ? isUnlimitedActive
                    ? "text-emerald-400"
                    : "text-primary"
                  : "text-text1"
              )
            }
            viewTransition
          >
            {({ isActive }) => (
              <>
                <GearSix size={iconSize} weight="duotone" />
                <span
                  className={cn(
                    isActive
                      ? isUnlimitedActive
                        ? "text-emerald-300"
                        : "text-text"
                      : "text-text1"
                  )}
                >
                  Settings
                </span>
              </>
            )}
          </NavLink>
        </div>
      </div>

      <Modal {...creditsModal.modalProps}>
        <GetCreditsModal countryCode={user?.country_code || "US"} />{" "}
        {/* Added optional chaining */}
      </Modal>
    </>
  );
}
